import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@lms/ui';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const authError = searchParams.get('error');
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [status] = useState<'success' | 'error'>(authError ? 'error' : 'success');
  const errorMessage = authError || t(copy.verifyEmail.generalError);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface font-body text-on-surface p-4 relative overflow-hidden">
      {/* Background Ambient Layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-surface to-surface-container-lowest -z-10" />
      <div className="absolute -top-48 -left-48 w-96 h-96 bg-primary opacity-[0.03] rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-secondary opacity-[0.02] rounded-full blur-[120px] pointer-events-none" />

      {/* Language Switcher */}
      <div className="absolute top-8 right-8 z-20">
        <div className="flex items-center bg-surface-container rounded-lg p-1 border border-on-surface/5">
          <button
            type="button"
            onClick={() => setLanguage('id')}
            className={`px-3 py-1 text-[11px] font-bold rounded transition-all ${
              language === 'id'
                ? 'bg-primary text-white shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            ID
          </button>
          <button
            type="button"
            onClick={() => setLanguage('ar')}
            className={`px-3 py-1 text-[11px] font-bold rounded transition-all ${
              language === 'ar'
                ? 'bg-primary text-white shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            AR
          </button>
        </div>
      </div>

      {/* Main Card */}
      <div className="w-full max-w-md bg-surface-container-lowest border border-outline-variant/20 rounded-3xl p-10 shadow-level3 relative z-10 flex flex-col items-center text-center animate-in fade-in zoom-in-95 duration-700">
        <img
          alt="Tarbiyah Heritage Logo"
          className="h-14 w-auto mb-10 opacity-90"
          src="/logo.png"
        />

        {status === 'success' && (
          <div className="flex flex-col items-center animate-in slide-in-from-bottom-4 fade-in duration-500">
            <div className="w-20 h-20 rounded-full bg-success-container/30 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-success" />
            </div>
            <h1 className="font-headline font-bold text-2xl text-on-surface mb-3">
              {t(copy.verifyEmail.successTitle)}
            </h1>
            <p className="text-on-surface-variant/80 text-sm mb-8">
              {t(copy.verifyEmail.successDesc)}
            </p>
            <Button
              onClick={() => navigate('/login')}
              variant="primary"
              size="lg"
              className="w-full rounded-xl flex items-center justify-center gap-3 group"
            >
              {t(copy.verifyEmail.successBtn)}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center animate-in slide-in-from-bottom-4 fade-in duration-500">
            <div className="w-20 h-20 rounded-full bg-error-container/20 flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-error" />
            </div>
            <h1 className="font-headline font-bold text-2xl text-on-surface mb-3">
              {t(copy.verifyEmail.failedTitle)}
            </h1>
            <p className="text-error/90 text-sm mb-8 px-4 font-medium bg-error-container/10 py-3 rounded-lg border border-error/10">
              {errorMessage}
            </p>
            <Button
              onClick={() => navigate('/login')}
              variant="secondary"
              size="lg"
              className="w-full rounded-xl"
            >
              {t(copy.verifyEmail.backToLogin)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
